const BRAND_BY_HOST = {
  'locations.jcpenney.com': 'jcpenney',
};

const EVENT_NAME_KEY = 'event_name';
const PAGE_VIEW_METADATA_KEY = '__yextAdobePageViewMetadata';
const PAGE_VIEW_PUSHED_KEY = '__yextAdobePageViewPushed';

const PASSTHROUGH_PARAM_NAMES = [
  'adobe_mc',
  'adobe_mc_sdid',
  'cid',
  'trackingCode',
  'tracking_code',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_audience',
  'utm_tactic',
  'utm_concept',
  'utm_content',
  'utm_sourcePlatform',
  'utm_source_platform',
  'utm_strategy',
  'utm_channel',
  'internalCampaignId',
  'internal_campaign_id',
  'icid',
  'profileId',
  'profile_id',
  'userId',
  'user_id',
  'visitorId',
  'visitor_id',
  'vid',
  'ecid',
  'mcmid',
  'sessionId',
  'session_id',
];

export class AdobeDataLayerPageView {
  static init(metadata = window[PAGE_VIEW_METADATA_KEY]) {
    if (
      !metadata
      || !BRAND_BY_HOST[metadata.siteInternalHostName]
      || window[PAGE_VIEW_PUSHED_KEY]
    ) {
      return;
    }

    const queryParams = this.parseQuery(window.location.search);
    const cleanUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
    const pageViewEvent = this.compact({
      event: 'pageView',
      [EVENT_NAME_KEY]: 'pageView',
      interactionName: 'regular_pageview',
      marketing: {
        trackingCode: this.getParam(queryParams, ['cid', 'trackingCode', 'tracking_code']),
        utmSource: this.getParam(queryParams, ['utm_source', 'utmSource']),
        utmMedium: this.getParam(queryParams, ['utm_medium', 'utmMedium']),
        utmName: this.getParam(queryParams, ['utm_campaign', 'utm_name', 'utmName']),
        utmTerm: this.getParam(queryParams, ['utm_term', 'utmTerm']),
        utmAudience: this.getParam(queryParams, ['utm_audience', 'utmAudience']),
        utmTactic: this.getParam(queryParams, ['utm_tactic', 'utmTactic']),
        utmConcept: this.getParam(queryParams, ['utm_concept', 'utmConcept']),
        utmContent: this.getParam(queryParams, ['utm_content', 'utmContent']),
        utmSourcePlatform: this.getParam(queryParams, [
          'utm_sourcePlatform',
          'utm_source_platform',
          'utmSourcePlatform',
        ]),
        utmStrategy: this.getParam(queryParams, ['utm_strategy', 'utmStrategy']),
        utmChannel: this.getParam(queryParams, ['utm_channel', 'utmChannel']),
        internalCampaignId: this.getParamList(queryParams, [
          'internalCampaignId',
          'internal_campaign_id',
          'icid',
        ]),
      },
      page: {
        affiliation: this.getAffiliation(),
        brand: BRAND_BY_HOST[metadata.siteInternalHostName],
        cleanUrl,
        host: window.location.hostname,
        language: 'en-US',
        breadcrumbs: this.getBreadcrumbs(metadata),
        name: this.getPageName(metadata),
        siteSection: 'store',
        storeType: metadata.storeType,
        type: this.getPageType(metadata.pageLevel),
        viewport: this.getViewport(),
        vendorEventId: this.getVendorEventId(cleanUrl),
      },
      user: {
        country: metadata.country || 'US',
        userId: this.getParam(queryParams, ['profileId', 'profile_id', 'userId', 'user_id']),
        visitorId: this.getParam(queryParams, [
          'visitorId',
          'visitor_id',
          'vid',
          'ecid',
          'mcmid',
        ]),
        sessionId: this.getParam(queryParams, ['sessionId', 'session_id']),
      },
    });

    window[PAGE_VIEW_PUSHED_KEY] = true;
    window.adobeDataLayer = window.adobeDataLayer || [];
    window.adobeDataLayer.push(pageViewEvent);
    this.preserveIncomingParams(queryParams);
    this.clearMetadata();
  }

  static parseQuery(search) {
    const params = {};
    const query = search ? search.replace(/^\?/, '') : '';

    if (!query) {
      return params;
    }

    query.split('&').filter(Boolean).forEach((pairString) => {
      const pair = pairString.split('=');
      const key = this.decode(pair.shift() || '').toLowerCase();
      const value = this.decode(pair.join('='));

      if (key) {
        params[key] = params[key] || [];
        params[key].push(value);
      }
    });

    return params;
  }

  static decode(value) {
    try {
      return decodeURIComponent(String(value || '').replace(/\+/g, ' '));
    } catch (e) {
      return value;
    }
  }

  static getParam(queryParams, names) {
    const matchedName = names
      .map((name) => String(name).toLowerCase())
      .find((name) => (
        queryParams[name]
        && queryParams[name].length
        && queryParams[name][0] !== ''
      ));

    return matchedName ? queryParams[matchedName][0] : undefined;
  }

  static getParamList(queryParams, names) {
    const seen = {};
    const result = [];

    names.forEach((name) => {
      const values = queryParams[String(name).toLowerCase()] || [];

      values.forEach((rawValue) => {
        String(rawValue).split(',').forEach((part) => {
          const value = this.trim(part);

          if (value && !seen[value]) {
            seen[value] = true;
            result.push(value);
          }
        });
      });
    });

    return result;
  }

  static getAffiliation() {
    const userAgent = window.navigator && window.navigator.userAgent
      ? window.navigator.userAgent
      : '';

    if (/android/i.test(userAgent)) {
      return 'android';
    }

    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return 'ios';
    }

    return window.innerWidth < 768 ? 'mobile' : 'desktop';
  }

  static getViewport() {
    const width = window.innerWidth || document.documentElement.clientWidth || 0;

    if (width < 768) {
      return 'small';
    }

    if (width < 1024) {
      return 'medium';
    }

    if (width < 1440) {
      return 'large';
    }

    return 'x-large';
  }

  static getPageName(metadata) {
    const brand = BRAND_BY_HOST[metadata.siteInternalHostName];
    const geo = this.getPageGeo(metadata);

    if (metadata.pageLevel === 'all' || metadata.pageLevel === 'locator') {
      return this.joinPageName(['stores', brand, 'home']);
    }

    if (metadata.pageLevel === 'state') {
      return this.joinPageName(['stores', brand, 'state', geo.state]);
    }

    if (metadata.pageLevel === 'city') {
      return this.joinPageName([
        'stores',
        brand,
        'city',
        geo.state,
        geo.city,
      ]);
    }

    if (metadata.pageLevel === 'location') {
      const pageName = this.joinPageName([
        'stores',
        brand,
        'storeselection',
        geo.state,
        geo.city,
      ]);
      const storeName = this.trim(geo.store);

      return storeName ? `${pageName}:${storeName}` : pageName;
    }

    return this.trim(metadata.pageTitle) || document.title || metadata.pageLevel;
  }

  static getBreadcrumbs(metadata) {
    if (metadata.pageLevel === 'locator') {
      return undefined;
    }

    const breadcrumbs = this.trim(metadata.breadcrumbs).replace(/\s*>\s*/g, '>');

    return breadcrumbs || undefined;
  }

  static getPageGeo(metadata) {
    return {
      state: metadata.region,
      city: metadata.city,
      store: metadata.storeName || metadata.locationName,
    };
  }

  static getPageType(pageLevel) {
    if (pageLevel === 'location') {
      return 'store';
    }

    if (pageLevel === 'locator') {
      return 'store locator';
    }

    if (pageLevel === 'all' || pageLevel === 'state' || pageLevel === 'city') {
      return 'store directory';
    }

    return pageLevel;
  }

  static getVendorEventId(url) {
    return `pageView:${url}:${new Date().getTime()}`;
  }

  static joinPageName(parts) {
    return parts
      .map((part) => this.trim(part))
      .filter(Boolean)
      .map((part) => part.toLowerCase())
      .join(':');
  }

  static trim(value) {
    return String(value || '').replace(/^\s+|\s+$/g, '');
  }

  static compact(value) {
    if (Object.prototype.toString.call(value) === '[object Array]') {
      const compactArray = value
        .map((arrayValue) => this.compact(arrayValue))
        .filter((arrayValue) => arrayValue !== undefined);

      return compactArray.length ? compactArray : undefined;
    }

    if (Object.prototype.toString.call(value) === '[object Object]') {
      const compactObject = {};

      Object.keys(value).forEach((key) => {
        const objectValue = this.compact(value[key]);

        if (objectValue !== undefined) {
          compactObject[key] = objectValue;
        }
      });

      return Object.keys(compactObject).length ? compactObject : undefined;
    }

    if (value === undefined || value === null || value === '') {
      return undefined;
    }

    return value;
  }

  static preserveIncomingParams(queryParams) {
    const paramsToPreserve = this.collectPassthroughParams(queryParams);

    if (!paramsToPreserve.length) {
      return;
    }

    this.onDomReady(() => {
      Array.from(document.querySelectorAll('a[href]')).forEach((link) => {
        this.appendParamsToInternalLink(link, paramsToPreserve);
      });
    });
  }

  static collectPassthroughParams(queryParams) {
    return PASSTHROUGH_PARAM_NAMES.reduce((params, name) => {
      const values = queryParams[String(name).toLowerCase()];

      if (values && values.length) {
        params.push({ name, values });
      }

      return params;
    }, []);
  }

  static onDomReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  static appendParamsToInternalLink(link, params) {
    const href = link.getAttribute('href');

    if (!href || /^(#|mailto:|tel:|javascript:)/i.test(href)) {
      return;
    }

    const parser = document.createElement('a');
    parser.href = href;

    if (parser.hostname !== window.location.hostname) {
      return;
    }

    const hashIndex = href.indexOf('#');
    const hash = hashIndex >= 0 ? href.slice(hashIndex) : '';
    const hrefWithoutHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
    const queryIndex = hrefWithoutHash.indexOf('?');
    const base = queryIndex >= 0
      ? hrefWithoutHash.slice(0, queryIndex)
      : hrefWithoutHash;
    const query = queryIndex >= 0 ? hrefWithoutHash.slice(queryIndex + 1) : '';
    const existingParams = this.parseQuery(query ? `?${query}` : '');
    const additions = [];

    params.forEach((param) => {
      if (existingParams[String(param.name).toLowerCase()]) {
        return;
      }

      param.values.forEach((value) => {
        additions.push(
          `${encodeURIComponent(param.name)}=${encodeURIComponent(value)}`,
        );
      });
    });

    if (additions.length) {
      link.setAttribute(
        'href',
        `${base}${query ? `?${query}&` : '?'}${additions.join('&')}${hash}`,
      );
    }
  }

  static clearMetadata() {
    try {
      delete window[PAGE_VIEW_METADATA_KEY];
    } catch (e) {
      window[PAGE_VIEW_METADATA_KEY] = undefined;
    }
  }
}
